import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Trophy, BookOpen, Flame, Award, Users, Globe } from "lucide-react";
import { LeaderboardList } from "@/components/leaderboard/LeaderboardList";

type LeaderboardType = 'xp' | 'books' | 'streak' | 'achievements';
type Timeframe = 'all-time' | 'month' | 'year';
type Scope = 'friends' | 'global';

export default function Leaderboards() {
  const [selectedType, setSelectedType] = useState<LeaderboardType>('xp');
  const [timeframe, setTimeframe] = useState<Timeframe>('all-time');
  const [scope, setScope] = useState<Scope>('friends');

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-8 w-full max-w-full overflow-x-hidden">
        <main className="container max-w-4xl mx-auto px-4 py-8 w-full">
          <div className="mb-6 text-center">
            <h1 className="text-4xl font-bold mb-2 flex items-center justify-center gap-3">
              <Trophy className="h-8 w-8 text-primary" />
              {scope === 'friends' ? 'Friends Leaderboard' : 'Community Leaderboard'}
            </h1>
            <p className="text-muted-foreground mb-4">
              {scope === 'friends' 
                ? 'See how you rank among people you follow! 🦕'
                : 'See how you rank among the Dino reading community. 🦕'}
            </p>
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => setScope('friends')}
                className={`px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors ${
                  scope === 'friends'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                }`}
              >
                <Users className="h-4 w-4" />
                Friends
              </button>
              <button
                onClick={() => setScope('global')}
                className={`px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors ${
                  scope === 'global'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                }`}
              >
                <Globe className="h-4 w-4" />
                Global
              </button>
            </div>
          </div>

          <Card className="p-6">
            <Tabs value={selectedType} onValueChange={(v) => setSelectedType(v as LeaderboardType)}>
              <TabsList className="grid w-full grid-cols-4 mb-6">
                <TabsTrigger value="xp" className="flex items-center gap-2">
                  <Trophy className="h-4 w-4" />
                  <span className="hidden sm:inline">Overall</span>
                </TabsTrigger>
                <TabsTrigger value="books" className="flex items-center gap-2">
                  <BookOpen className="h-4 w-4" />
                  <span className="hidden sm:inline">Books</span>
                </TabsTrigger>
                <TabsTrigger value="streak" className="flex items-center gap-2">
                  <Flame className="h-4 w-4" />
                  <span className="hidden sm:inline">Streaks</span>
                </TabsTrigger>
                <TabsTrigger value="achievements" className="flex items-center gap-2">
                  <Award className="h-4 w-4" />
                  <span className="hidden sm:inline">Achievements</span>
                </TabsTrigger>
              </TabsList>

              {selectedType === 'books' && (
                <div className="flex gap-2 mb-6 justify-center">
                  <button
                    onClick={() => setTimeframe('year')}
                    className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                      timeframe === 'year'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                    }`}
                  >
                    This Year
                  </button>
                  <button
                    onClick={() => setTimeframe('month')}
                    className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                      timeframe === 'month'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                    }`}
                  >
                    This Month
                  </button>
                  <button
                    onClick={() => setTimeframe('all-time')}
                    className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                      timeframe === 'all-time'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                    }`}
                  >
                    All Time
                  </button>
                </div>
              )}

              <TabsContent value="xp">
                <LeaderboardList type="xp" timeframe={timeframe} scope={scope} />
              </TabsContent>
              
              <TabsContent value="books">
                <LeaderboardList type="books" timeframe={timeframe} scope={scope} />
              </TabsContent>
              
              <TabsContent value="streak">
                <LeaderboardList type="streak" timeframe={timeframe} scope={scope} />
              </TabsContent>
              
              <TabsContent value="achievements">
                <LeaderboardList type="achievements" timeframe={timeframe} scope={scope} />
              </TabsContent>
            </Tabs>
          </Card>
        </main>
      </div>
  );
}
